import type { ReactNode } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import {
  ChevronDown16Regular,
  ChevronUp16Regular,
} from '@fluentui/react-icons';
import { useState } from 'react';
import { headingIconColor, headingStyle } from '../theme/headingTheme';
import MokoCard from './MokoCard';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  icon: {
    display: 'inline-flex',
    fontSize: '20px',
    color: headingIconColor,
  },
  title: {
    ...headingStyle,
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
  },
  titleButton: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
    padding: 0,
    border: 0,
    backgroundColor: 'transparent',
    color: 'inherit',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    marginLeft: 'auto',
  },
  body: {
    minWidth: 0,
    padding: tokens.spacingHorizontalXL,
  },
});

interface TitledCardProps {
  title: string;
  icon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  collapsible?: boolean;
  defaultOpen?: boolean;
}

const TitledCard = ({
  title,
  icon,
  actions,
  children,
  className,
  collapsible = false,
  defaultOpen = true,
}: TitledCardProps) => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <MokoCard
      className={className ? `${styles.card} ${className}` : styles.card}
      style={{ padding: 0 }}
    >
      <header className={styles.header}>
        {collapsible ? (
          <h2 className={styles.title}>
            <button
              type="button"
              className={styles.titleButton}
              aria-expanded={isOpen}
              onClick={() => setIsOpen((open) => !open)}
            >
              {icon && <span className={styles.icon}>{icon}</span>}
              <span>{title}</span>
              {isOpen ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
            </button>
          </h2>
        ) : (
          <>
            {icon && <span className={styles.icon}>{icon}</span>}
            <h2 className={styles.title}>{title}</h2>
          </>
        )}
        {actions && <div className={styles.actions}>{actions}</div>}
      </header>
      {(!collapsible || isOpen) && (
        <div className={styles.body}>{children}</div>
      )}
    </MokoCard>
  );
};

export default TitledCard;
